import React, {useState, useRef} from "react";
import {
    Box, Stack, Typography, ToggleButtonGroup, ToggleButton, Button, Popover, TextField, Autocomplete, Switch,
    FormControlLabel, IconButton, Tooltip, InputAdornment, Chip, Menu, MenuItem, Divider, Dialog, DialogTitle,
    DialogContent, DialogActions, Badge, LinearProgress,
} from "@mui/material";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import {MobileDatePicker} from "@mui/x-date-pickers/MobileDatePicker";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import TuneIcon from "@mui/icons-material/Tune";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";
import {fmtRange, utcYesterday, shortProvider, EMPTY, PRESETS, presetLabel} from "./format";
import {TYPE, CONTROL_H, NEUTRAL, TABULAR} from "./tokens";

/** Phone chrome: 44 px header (range · search · filters · refresh) + 44 px tab row; both sticky → content sticks under 88 px. */
export const PHONE_HEADER_H = 44;
export const PHONE_TABS_H = 44;
export const PHONE_STACK = PHONE_HEADER_H + PHONE_TABS_H;

/** Local-calendar dayjs for a YYYY-MM-DD string (the pickers work in local time; we only exchange date strings). */
const toLocal = (s) => (s ? dayjs(s) : null);
const maxPickerDate = () => dayjs(utcYesterday().format("YYYY-MM-DD"));

const controlSx = {
    "& .MuiInputBase-root": {minHeight: CONTROL_H, fontSize: 13, bgcolor: "background.paper"},
    "& .MuiOutlinedInput-input": {py: "5px"},
};
const autoSx = {
    ...controlSx,
    // nowrap: the selection summary and the input stay on one line so the header never grows a second row.
    "& .MuiAutocomplete-inputRoot": {py: "1px !important", pl: "8px !important", flexWrap: "nowrap"},
    // 24 px floor: with a selection the summary needs the room; the caret still lands in the input to type a filter.
    "& .MuiAutocomplete-input": {py: "3px !important", minWidth: "24px !important"},
};

/** Selection summary in place of chips: "CSE" or "CSE +2" — one line, never wraps; the label truncates, the count never does. */
const renderSummary = (label) => (value) => (
    value.length ? (
        <Box component="span" title={value.map(label).join(", ")}
             sx={{display: "flex", alignItems: "center", flex: "1 1 auto", minWidth: 0, pl: 1, gap: 0.5, fontSize: 13, ...TABULAR}}>
            <Typography component="span" noWrap sx={{fontSize: "inherit", minWidth: 0}}>{label(value[0])}</Typography>
            {value.length > 1 && <Typography component="span" sx={{fontSize: "inherit", flex: "0 0 auto", color: "text.secondary"}}>+{value.length - 1}</Typography>}
        </Box>
    ) : null
);
const renderGroupTags = renderSummary((g) => g.label);
const renderProviderTags = renderSummary((v) => shortProvider(v));

const toggleSx = {
    "& .MuiToggleButton-root": {
        height: CONTROL_H, px: 1.25, fontSize: 12.5, fontWeight: 600, textTransform: "none", lineHeight: 1, ...TABULAR,
        color: "text.secondary", borderColor: NEUTRAL.divider,
        "&.Mui-selected": {color: "primary.main", bgcolor: "rgba(25,118,210,.08)"},
    },
};

// ---------------------------------------------------------------------------------------------------------------------

/** Two date pickers + Apply, anchored to the range button. */
export const RangePopover = ({anchorEl, open, onClose, from, to, onApply}) => {
    const [f, setF] = useState(() => toLocal(from));
    const [t, setT] = useState(() => toLocal(to));
    const maxDate = maxPickerDate();
    const valid = f && t && f.isValid() && t.isValid() && !f.isAfter(t) && !t.isAfter(maxDate);
    return (
        <Popover open={open} anchorEl={anchorEl} onClose={onClose}
                 anchorOrigin={{vertical: "bottom", horizontal: "left"}} transformOrigin={{vertical: "top", horizontal: "left"}}
                 PaperProps={{sx: {p: 2, mt: 0.5, width: 340}}}>
            <Typography sx={{...TYPE.eyebrow, mb: 1.5}}>Closed days (UTC)</Typography>
            <Stack direction="row" spacing={1.5}>
                <DatePicker label="From" value={f} format="YYYY-MM-DD" maxDate={t || maxDate}
                            onChange={(d) => setF(d)} slotProps={{textField: {size: "small", fullWidth: true}}}/>
                <DatePicker label="To" value={t} format="YYYY-MM-DD" minDate={f || undefined} maxDate={maxDate}
                            onChange={(d) => setT(d)} slotProps={{textField: {size: "small", fullWidth: true}}}/>
            </Stack>
            <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{mt: 2}}>
                <Button size="small" onClick={onClose} sx={{textTransform: "none"}}>Cancel</Button>
                <Button size="small" variant="contained" disableElevation disabled={!valid} sx={{textTransform: "none"}}
                        onClick={() => { onApply(f.format("YYYY-MM-DD"), t.format("YYYY-MM-DD")); onClose(); }}>
                    Apply
                </Button>
            </Stack>
        </Popover>
    );
};

/** Phone: full-screen-ish dialog with two MobileDatePickers. */
export const RangeDialog = ({open, onClose, from, to, onApply}) => {
    const [f, setF] = useState(() => toLocal(from));
    const [t, setT] = useState(() => toLocal(to));
    const maxDate = maxPickerDate();
    const valid = f && t && f.isValid() && t.isValid() && !f.isAfter(t) && !t.isAfter(maxDate);
    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle sx={{fontSize: 16, fontWeight: 600, pb: 1}}>Custom range</DialogTitle>
            <DialogContent sx={{pt: "8px !important"}}>
                <Stack spacing={2} sx={{mt: 0.5}}>
                    <MobileDatePicker label="From" value={f} format="MMM D" maxDate={t || maxDate}
                                      onChange={(d) => setF(d)} slotProps={{textField: {size: "small", fullWidth: true}}}/>
                    <MobileDatePicker label="To" value={t} format="MMM D" minDate={f || undefined} maxDate={maxDate}
                                      onChange={(d) => setT(d)} slotProps={{textField: {size: "small", fullWidth: true}}}/>
                    <Typography sx={TYPE.caption}>Closed days in UTC · latest {maxDate.format("MMM D")}</Typography>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} sx={{textTransform: "none"}}>Cancel</Button>
                <Button variant="contained" disableElevation disabled={!valid} sx={{textTransform: "none"}}
                        onClick={() => { onApply(f.format("YYYY-MM-DD"), t.format("YYYY-MM-DD")); onClose(); }}>
                    Apply
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ---------------------------------------------------------------------------------------------------------------------

/**
 * Desktop / tablet header: title · presets · range · prev caption · search · network · partner · silent tags · CSV.
 */
export const VIEWS = [{key: "panel", label: "Panel"}, {key: "heatmap", label: "Heatmap"}];

const HeaderBar = ({
    range, preset, onPreset, onRange,
    search, onSearch, groupOptions, groups, onGroups, providerOptions, providers, onProviders,
    includeInactive, onIncludeInactive, onExport, canExport, view = "panel", onView, extra = null,
}) => {
    const [rangeAnchor, setRangeAnchor] = useState(null);
    const rangeLabel = range ? fmtRange(range.from, range.to) : EMPTY;
    const prevLabel = range && range.prev_from ? fmtRange(range.prev_from, range.prev_to) : null;
    const selectedGroups = groupOptions.filter((g) => groups.includes(g.prefix));

    return (
        <Box sx={{display: "flex", flexWrap: "wrap", alignItems: "center", columnGap: 1, rowGap: 1, minHeight: 40}}>
            <Typography component="h1" sx={{...TYPE.title, mr: 0.5, whiteSpace: "nowrap"}}>Traffic Control</Typography>

            {onView && (
                <ToggleButtonGroup exclusive size="small" value={view} onChange={(e, v) => v && onView(v)} sx={toggleSx} aria-label="View">
                    {VIEWS.map((v) => <ToggleButton key={v.key} value={v.key} aria-label={`${v.label} view`}>{v.label}</ToggleButton>)}
                </ToggleButtonGroup>
            )}

            <ToggleButtonGroup exclusive size="small" value={preset} onChange={(e, v) => v && onPreset(v)} sx={toggleSx} aria-label="Range preset">
                {PRESETS.map((p) => <ToggleButton key={p.key} value={p.key} aria-label={p.long}>{p.label}</ToggleButton>)}
            </ToggleButtonGroup>

            <Button size="small" variant="outlined" color="inherit" onClick={(e) => setRangeAnchor(e.currentTarget)}
                    endIcon={<ArrowDropDownIcon/>} aria-label={`Range ${rangeLabel}, change`}
                    sx={{height: CONTROL_H, textTransform: "none", fontSize: 13, fontWeight: 600, borderColor: NEUTRAL.divider, color: "text.primary", px: 1.25, ...TABULAR}}>
                {rangeLabel}
            </Button>
            {rangeAnchor && (
                <RangePopover anchorEl={rangeAnchor} open onClose={() => setRangeAnchor(null)} from={range?.from} to={range?.to} onApply={onRange}/>
            )}
            {prevLabel && (
                <Typography sx={{...TYPE.caption, ...TABULAR, whiteSpace: "nowrap"}}>vs {prevLabel}</Typography>
            )}
            <Box sx={{flexGrow: 1}}/>

            <TextField size="small" value={search} onChange={(e) => onSearch(e.target.value)} placeholder="tag / site"
                       inputProps={{"aria-label": "Search tag or site"}}
                       InputProps={{
                           startAdornment: <InputAdornment position="start"><SearchIcon sx={{fontSize: 18, color: "text.disabled"}}/></InputAdornment>,
                           endAdornment: search ? (
                               <InputAdornment position="end">
                                   <IconButton size="small" aria-label="Clear search" onClick={() => onSearch("")} sx={{p: 0.25}}><CloseIcon sx={{fontSize: 16}}/></IconButton>
                               </InputAdornment>
                           ) : null,
                       }}
                       sx={{...controlSx, flex: "1 1 130px", maxWidth: 150}}/>

            <Autocomplete multiple size="small" options={groupOptions} value={selectedGroups}
                          getOptionLabel={(g) => g.label} isOptionEqualToValue={(a, b) => a.prefix === b.prefix}
                          onChange={(e, v) => onGroups(v.map((g) => g.prefix))} renderTags={renderGroupTags}
                          renderInput={(p) => <TextField {...p} placeholder={selectedGroups.length ? "" : "Channel"} inputProps={{...p.inputProps, "aria-label": "Channel filter"}}/>}
                          sx={{...autoSx, flex: "1 1 150px", maxWidth: 180}}/>

            <Autocomplete multiple size="small" options={providerOptions} value={providers}
                          getOptionLabel={(v) => shortProvider(v)} onChange={(e, v) => onProviders(v)} renderTags={renderProviderTags}
                          renderInput={(p) => <TextField {...p} placeholder={providers.length ? "" : "Partner"} inputProps={{...p.inputProps, "aria-label": "Partner filter"}}/>}
                          sx={{...autoSx, flex: "1 1 150px", maxWidth: 180}}/>

            <Tooltip title="Tags with nothing sent and $0 in the range" arrow enterDelay={400}>
                <FormControlLabel sx={{mr: 0, ml: 0, "& .MuiFormControlLabel-label": {fontSize: 12.5, color: "text.secondary", whiteSpace: "nowrap", ...TABULAR}}}
                                  control={<Switch size="small" checked={includeInactive} onChange={(e) => onIncludeInactive(e.target.checked)} inputProps={{"aria-label": "Include silent tags"}}/>}
                                  label="Include silent tags"/>
            </Tooltip>

            {extra}

            <Tooltip title="Export CSV">
                <span>
                    <IconButton size="small" aria-label="Export CSV" onClick={onExport} disabled={!canExport} sx={{width: CONTROL_H, height: CONTROL_H}}>
                        <DownloadIcon sx={{fontSize: 20}}/>
                    </IconButton>
                </span>
            </Tooltip>
        </Box>
    );
};

export default HeaderBar;

// ---------------------------------------------------------------------------------------------------------------------

/**
 * Phone control row (48 px, sticky): range chip + menu, search (inline), filters badge, refresh. LinearProgress at the bottom.
 */
export const PhoneHeader = ({
    range, preset, onPreset, onRange, search, onSearch, activeFilters, onOpenFilters, onRefresh, loading,
}) => {
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [customOpen, setCustomOpen] = useState(false);
    const [searching, setSearching] = useState(Boolean(search));
    const inputRef = useRef(null);
    const rangeLabel = range ? fmtRange(range.from, range.to) : EMPTY;
    const chipLabel = presetLabel(preset) ? `${presetLabel(preset)} · ${rangeLabel}` : rangeLabel;

    return (
        <Box sx={{
            position: "sticky", top: 0, zIndex: 4, height: PHONE_HEADER_H, boxSizing: "border-box", bgcolor: "background.paper",
            borderBottom: `1px solid ${NEUTRAL.hairline}`, display: "flex", alignItems: "center", px: 1, gap: 0.5,
        }}>
            {searching ? (
                <>
                    <TextField size="small" autoFocus fullWidth value={search} onChange={(e) => onSearch(e.target.value)} placeholder="tag / site"
                               inputRef={inputRef} inputProps={{"aria-label": "Search tag or site"}}
                               InputProps={{startAdornment: <InputAdornment position="start"><SearchIcon sx={{fontSize: 18, color: "text.disabled"}}/></InputAdornment>}}
                               sx={{"& .MuiInputBase-root": {height: 36}, "& .MuiInputBase-input": {fontSize: 16}}}/>
                    <Button size="small" onClick={() => { onSearch(""); setSearching(false); }} sx={{textTransform: "none", flex: "0 0 auto"}}>Cancel</Button>
                </>
            ) : (
                <>
                    <Chip icon={<CalendarMonthIcon sx={{fontSize: 16}}/>} label={chipLabel} variant="outlined" clickable
                          onClick={(e) => setMenuAnchor(e.currentTarget)} onDelete={(e) => setMenuAnchor(e.currentTarget.closest(".MuiChip-root"))}
                          deleteIcon={<ArrowDropDownIcon/>} aria-label={`Range ${chipLabel}, change`}
                          sx={{height: 32, fontSize: 13, fontWeight: 600, ...TABULAR, maxWidth: "calc(100% - 128px)", "& .MuiChip-label": {overflow: "hidden", textOverflow: "ellipsis"}}}/>
                    <Box sx={{flexGrow: 1}}/>
                    <IconButton aria-label="Search" onClick={() => setSearching(true)} sx={{width: 40, height: 40}}><SearchIcon/></IconButton>
                    <IconButton aria-label={`Filters${activeFilters ? `, ${activeFilters} active` : ""}`} onClick={onOpenFilters} sx={{width: 40, height: 40}}>
                        <Badge badgeContent={activeFilters} color="primary" overlap="circular"
                               sx={{"& .MuiBadge-badge": {fontSize: 10, height: 16, minWidth: 16}}}>
                            <TuneIcon/>
                        </Badge>
                    </IconButton>
                    <IconButton aria-label="Refresh" onClick={onRefresh} sx={{width: 40, height: 40}}><RefreshIcon/></IconButton>
                </>
            )}
            <Menu open={Boolean(menuAnchor)} anchorEl={menuAnchor} onClose={() => setMenuAnchor(null)}>
                {PRESETS.map((p) => (
                    <MenuItem key={p.key} selected={preset === p.key} onClick={() => { setMenuAnchor(null); onPreset(p.key); }} sx={{fontSize: 14}}>
                        {p.long}
                    </MenuItem>
                ))}
                <Divider/>
                <MenuItem onClick={() => { setMenuAnchor(null); setCustomOpen(true); }} sx={{fontSize: 14}}>Custom…</MenuItem>
            </Menu>
            {customOpen && <RangeDialog open onClose={() => setCustomOpen(false)} from={range?.from} to={range?.to} onApply={onRange}/>}
            {loading && <LinearProgress sx={{position: "absolute", left: 0, right: 0, bottom: -1, height: 2}}/>}
        </Box>
    );
};
